import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommentsService } from '@home/src/app/core/services/comments/comments.service';
import { SingleCommentComponent } from '../single-comment/single-comment.component';
import { FormsModule } from '@angular/forms';
import { CustomDropdownComponent } from '../custom-dropdown/custom-dropdown.component';
import { GlobalService } from '@home/src/app/core/services/global/global.service';
import { GroupMember } from '@home/src/app/Interface/Dealer';
import { IMessage } from '@home/src/app/Interface/messages';
import { Subject, takeUntil } from 'rxjs';
import { SignalrService } from '@home/src/app/core/services/signalr/signalr.service';
import { MessageActions } from '@home/src/app/shared/constants/signalr-constants';
import { AuthenticationService } from '@home/src/app/core/services/Auth/authservice';
import { MESSAGE_EVENT_NAME } from '@home/src/app/shared/constants/signalr-constants';

@Component({
  selector: 'app-comments-section',
  standalone: true,
  imports: [SingleCommentComponent, FormsModule, CustomDropdownComponent],
  templateUrl: './comments-section.component.html',
  styleUrl: './comments-section.component.css',
})
export class CommentsSectionComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  enableAddComment: boolean = false;
  isOpen: boolean = false;
  showAddForm: boolean = true;

  title: string = 'Messages';
  subject: string | null = '';
  type: string | null = '';
  messages: IMessage[] = [];
  newComment: string = '';
  errorMessage: string = '';

  groupMembersList: any[] = [];
  selectedMembers: any[] = [];
  authUser: any = null;

  dropdownOpen: boolean = false;
  filteredMessages: IMessage[] = [];

  constructor(
    private commentsService: CommentsService,
    private globalService: GlobalService,
    private signalrService: SignalrService,
    private authService: AuthenticationService,
    private cdr: ChangeDetectorRef,
  ) {}

  get sendToMembers(): GroupMember[] {
    return this.groupMembersList.filter(
      (dealer) => dealer.emailAddress !== this.authUser?.email,
    );
  }

  ngOnInit() {
    this.authService.fincomData$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.authUser = state;
      });
    this.signalrService.connectionState$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        if (state === 'connected') {
          this.signalrService.on(MESSAGE_EVENT_NAME, (data) =>
            this.newMessage(data),
          );
        }
      });
    this.commentsService.groupMembersList$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (value && value.length) {
          this.groupMembersList = value.map((member: any) => {
            return {
              ...member,
              display: `${member.dealerName} (${member.emailAddress})`,
            };
          });
        }
      });
    this.commentsService.title$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.title = value;
      });
    this.commentsService.type$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.type = value;
      });
    this.commentsService.subject$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.subject = value;
      });
    this.commentsService.messages$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.messages = value;
        this.updateFilteredMessages();
      });
    this.commentsService.isOpen$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.isOpen = value;
        if (this.isOpen) this.updateFilteredMessages();
      });
    this.commentsService.enableAddComment$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.enableAddComment = value;
      });
  }

  getDealerName(code: string): string | undefined {
    return this.groupMembersList.find((dealer) => dealer.emailAddress === code)
      ?.dealerName;
  }

  async newMessage(data: any) {
    if (!data || !data.action) return;
    if (data.action === MessageActions.inserted) {
      await this.commentsService.getAllMessages(this.authUser?.email);
      const latestMessage = this.messages.find(
        (msg) => msg.commentId === data.commentId,
      );
      if (
        latestMessage?.memberEmails.some(
          (item) => item === this.authUser?.email,
        )
      ) {
        this.globalService.showToast(
          `New message from ${this.getDealerName(latestMessage?.createdBy || '')}`,
          'success',
        );
      }
    } else if (data.action === MessageActions.deleted) {
      await this.commentsService.getAllMessages(this.authUser?.email);
    } else if (data.action === MessageActions.marked_as_read) {
      await this.commentsService.getAllMessages(this.authUser?.email);
    }
  }

  onDropdownToggle(open: boolean) {
    this.dropdownOpen = open;
  }

  onDealersSelected(event: GroupMember[]) {
    this.selectedMembers = [...event];
    this.errorMessage = '';
  }

  closePanel() {
    const unreadMessageIds = this.filteredMessages
      .filter((msg) => !msg.isRead)
      .map((msg) => msg.commentId);
    if (unreadMessageIds.length)
      this.commentsService.markMessagesAsRead(
        unreadMessageIds,
        this.authUser?.email,
      );
    this.commentsService.setOpenCommentsPanel(false);
    this.newComment = '';
    this.errorMessage = '';
    this.selectedMembers = [];
  }

  updateFilteredMessages() {
    if (this.messages.length === 0) {
      this.filteredMessages = [];
      this.cdr.detectChanges();
      return;
    }
    if (this.type === null) {
      this.commentsService.setTitle('Messages');
      this.commentsService.setEnableAddComment(false);
      this.commentsService.setSubhect(null);
      this.filteredMessages = [...this.messages];
    } else {
      this.filteredMessages = this.messages.filter(
        (msg) => msg.commentType === this.type,
      );
    }
    this.cdr.markForCheck();
  }

  async onDeleteMessage(id: number) {
    console.log('Delete message with ID:', id);
    const response: any = await this.commentsService.deleteComment(id);
    if (response?.statusCode === 200) {
      this.globalService.showToast('Message deleted successfully', 'success');
    } else {
      this.globalService.showToast('Failed to delete message.', 'error');
    }
  }

  async submitComment() {
    this.errorMessage = '';
    if (this.selectedMembers.length === 0) {
      this.errorMessage =
        'Please select at least one dealer to send the message to.';
      return;
    }
    if (this.newComment.trim() === '') {
      this.errorMessage = 'Message text cannot be empty.';
      return;
    }
    const payload = {
      commentType: this.type,
      subject: this.subject,
      commentText: this.newComment,
      memberEmails: this.selectedMembers.map((dealer) => dealer.emailAddress),
      emailAddress: this.authUser.email,
    };
    const response: any = await this.commentsService.addNewComment(payload);
    if (response?.statusCode === 200) {
      this.globalService.showToast('Message sent successfully', 'success');
      this.newComment = '';
      this.selectedMembers = [];
    } else {
      this.globalService.showToast('Failed to send message.', 'error');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.complete();
  }
}
